/**
 * VISTOERA_CANVAS_GENERATE_VIDEO_SEMANTIC_DROP_ZONES_EDGE_PORT_ALIGNMENT_AND_COLLAPSIBLE_REFERENCE_ASSETS_P0_V1
 * §三 "Canonical Generate Video Input Port Contract" — ONE registry
 * describing every semantic input a `generate_video` node accepts,
 * shared by THREE consumers that must never drift apart (spec §一
 * "DROP TARGET == EDGE TARGET == STRUCTURED INPUT PORT"):
 *
 *   1. React Flow Handle ids — the PRE-EXISTING canonical id functions
 *      in `dataflow.ts` (`videoStructuredAssetHandleId`,
 *      `VIDEO_REFERENCE_VIDEO_HANDLE`, `VIDEO_KEYFRAME_START_HANDLE`/
 *      `VIDEO_KEYFRAME_END_HANDLE`/`videoKeyframeMiddleHandleId`) — this
 *      file never invents a second id scheme, only resolves DOM
 *      pointer position TO them.
 *   2. DOM Drop Zones — the `data-testid`/`data-keyframe-*` anchors
 *      `VideoInputAssetsSection.tsx` / `KeyframeTimelineStrip.tsx` /
 *      `GenerateVideoNode.tsx`'s reference-video section already
 *      render for OTHER reasons (handle-geometry measurement, keyframe
 *      selection) — reused verbatim as hit-test targets, never a
 *      second/parallel set of anchors.
 *   3. Structured assignment builders — `buildVideoStructuredAssetAssignment`/
 *      `buildVideoReferenceVideoAssignment`/`keyframeAssignmentByNodeId`
 *      (`CanvasBoard.tsx`) already key off the SAME handle ids, so
 *      resolving to one of THOSE ids here means a drop-zone-resolved
 *      synthetic connection produces the IDENTICAL structured
 *      assignment a Handle-precise drag would (spec §七 "必须满足...
 *      从 Image Node 拉线到 Character Handle：必须和拖图片到 Character
 *      Drop Zone 得到完全相同 assignment").
 *
 * `resolveGenerateVideoDropTarget` is the ONE hit-test entry point
 * `CanvasBoard.tsx`'s `onConnectEnd` drop-zone fallback calls; it is
 * pure (no DOM globals referenced directly) so it can run against a
 * fake `DropZoneProbeElement` in plain-Node tests (spec §18 test
 * matrix), never requiring jsdom.
 */

import {
  GENERIC_INPUT_HANDLE,
  VIDEO_AUDIO_REFERENCE_HANDLE,
  VIDEO_KEYFRAME_END_HANDLE,
  VIDEO_KEYFRAME_START_HANDLE,
  VIDEO_ORIGINAL_PROMPT_IN_HANDLE,
  VIDEO_REFERENCE_VIDEO_HANDLE,
  isVideoAudioReferenceHandle,
  isVideoKeyframeHandle,
  isVideoReferenceVideoHandle,
  isVideoStructuredAssetHandle,
  videoKeyframeMiddleHandleId,
  videoStructuredAssetHandleId,
  type VideoStructuredAssetCategory,
} from './dataflow'
import { VIDEO_ORIGINAL_PROMPT_SECTION_TESTID } from './videoInputPortRegistry'

/** VISTOERA_GENERATE_VIDEO_DROP_ZONE_IS_TARGET_PORT_AND_COLLAPSED_VISUAL_PROXY_P0_V3
 * §一/§五 — the DOM attribute a Drop Zone declares ITS OWN canonical
 * port on (`VideoInputAssetsSection.tsx`'s category rows,
 * `GenerateVideoNode.tsx`'s prompt/reference-video sections,
 * `KeyframeTimelineStrip.tsx`'s chip wraps). This is now the PRIMARY —
 * not a secondary/derived — source of truth for hit-testing: no
 * testid-parsing, no category-name remapping, no second port
 * vocabulary. Drop Zone and Handle share the identical id by
 * construction (both are handed the SAME canonical port constant). */
export const VIDEO_INPUT_PORT_ATTR = 'data-video-input-port'

/** Minimal DOM contract this module needs — a real `Element` already
 * structurally satisfies it (`closest`/`getAttribute`); node-tests pass
 * a plain fake object implementing just these two methods. */
export interface DropZoneProbeElement {
  closest(selector: string): DropZoneProbeElement | null
  getAttribute(name: string): string | null
}

/** `VideoInputAssetsSection.tsx`'s own per-category row anchor —
 * `data-testid={`video-input-asset-category-${category}`}` — reused
 * verbatim, never re-derived. */
export const VIDEO_ASSET_CATEGORY_TESTID_PREFIX = 'video-input-asset-category-'
/** `GenerateVideoNode.tsx`'s reference-video section anchor. */
export const VIDEO_REFERENCE_VIDEO_SECTION_TESTID = 'video-reference-video-section'
/** `GenerateVideoNode.tsx`'s 音频 (audio reference) section anchor
 * (VistoeraTASK-00143 §五). */
export const VIDEO_AUDIO_REFERENCE_SECTION_TESTID = 'video-audio-reference-section'
/** `KeyframeDetailPanel.tsx`'s root anchor — carries
 * `data-keyframe-entry-id` for the CURRENTLY SELECTED keyframe (the
 * only one ever mounted, spec §7's "只有一个 Detail Panel 挂载"). */
export const VIDEO_KEYFRAME_DETAIL_TESTID = 'video-keyframe-detail'
export const VIDEO_KEYFRAME_ENTRY_ID_ATTR = 'data-keyframe-entry-id'
/** `KeyframeTimelineStrip.tsx`'s per-chip anchor. */
export const VIDEO_KEYFRAME_CHIP_ID_ATTR = 'data-keyframe-chip-id'

const CATEGORY_TESTID_VALUES: readonly VideoStructuredAssetCategory[] = ['character', 'object', 'scene', 'reference']

export type VideoDropZoneKind =
  | 'structured-asset'
  | 'reference-video'
  | 'audio-reference'
  | 'keyframe'
  | 'prompt'
  | 'reference-tray'

export interface ResolvedVideoDropTarget {
  targetHandle: string
  zoneKind: VideoDropZoneKind
}

/** Pure DOM hit-test resolver (spec §四/§五/§六/§七) — given the element
 * under the pointer at connection-drop time, walks UP through the SAME
 * anchors the node's own JSX renders for other purposes, and returns
 * the canonical target handle id a real Handle-precise drag to that
 * same visual region would produce.
 *
 * Returns `null` for anything that is NOT an explicit semantic zone
 * (spec §四 — the top video-result/media-body area, the Provider/Model
 * footer, the collapsed Reference Assets header itself, etc. must
 * NEVER resolve to a handle here — no ambiguous/implicit target ever
 * falls through to a default port, spec §八). */
function classifyVideoInputPort(targetHandle: string): VideoDropZoneKind | null {
  if (targetHandle === VIDEO_ORIGINAL_PROMPT_IN_HANDLE) return 'prompt'
  if (targetHandle === GENERIC_INPUT_HANDLE) return 'reference-tray'
  if (isVideoReferenceVideoHandle(targetHandle)) return 'reference-video'
  if (isVideoAudioReferenceHandle(targetHandle)) return 'audio-reference'
  if (isVideoKeyframeHandle(targetHandle)) return 'keyframe'
  if (isVideoStructuredAssetHandle(targetHandle)) return 'structured-asset'
  return null
}

export function resolveGenerateVideoDropTarget(el: DropZoneProbeElement | null): ResolvedVideoDropTarget | null {
  if (!el) return null

  // §一/§五 PRIMARY PATH — the Drop Zone declares its OWN canonical port
  // directly via `data-video-input-port` (identical id the SAME
  // component hands its own DOM-owned `<Handle>`). No testid parsing,
  // no category-name remapping — one attribute IS the port.
  const portEl = el.closest(`[${VIDEO_INPUT_PORT_ATTR}]`)
  const declaredPort = portEl?.getAttribute(VIDEO_INPUT_PORT_ATTR)
  if (declaredPort) {
    const zoneKind = classifyVideoInputPort(declaredPort)
    if (zoneKind) return { targetHandle: declaredPort, zoneKind }
  }

  // Defensive fallback (never the primary path) — covers any zone that
  // has not yet been updated to declare `data-video-input-port` itself.
  const categoryRow = el.closest(`[data-testid^="${VIDEO_ASSET_CATEGORY_TESTID_PREFIX}"]`)
  if (categoryRow) {
    const testid = categoryRow.getAttribute('data-testid') || ''
    const category = testid.slice(VIDEO_ASSET_CATEGORY_TESTID_PREFIX.length) as VideoStructuredAssetCategory
    if ((CATEGORY_TESTID_VALUES as string[]).includes(category)) {
      const detailPanel = el.closest(`[data-testid="${VIDEO_KEYFRAME_DETAIL_TESTID}"]`)
      const keyframeId = detailPanel?.getAttribute(VIDEO_KEYFRAME_ENTRY_ID_ATTR) || undefined
      return { targetHandle: videoStructuredAssetHandleId(category, keyframeId), zoneKind: 'structured-asset' }
    }
  }

  if (el.closest(`[data-testid="${VIDEO_REFERENCE_VIDEO_SECTION_TESTID}"]`)) {
    return { targetHandle: VIDEO_REFERENCE_VIDEO_HANDLE, zoneKind: 'reference-video' }
  }

  if (el.closest(`[data-testid="${VIDEO_AUDIO_REFERENCE_SECTION_TESTID}"]`)) {
    return { targetHandle: VIDEO_AUDIO_REFERENCE_HANDLE, zoneKind: 'audio-reference' }
  }

  if (el.closest(`[data-testid="${VIDEO_ORIGINAL_PROMPT_SECTION_TESTID}"]`)) {
    return { targetHandle: VIDEO_ORIGINAL_PROMPT_IN_HANDLE, zoneKind: 'prompt' }
  }

  const chip = el.closest(`[${VIDEO_KEYFRAME_CHIP_ID_ATTR}]`)
  if (chip) {
    const testid = chip.getAttribute('data-testid') || ''
    if (testid.endsWith('-start')) return { targetHandle: VIDEO_KEYFRAME_START_HANDLE, zoneKind: 'keyframe' }
    if (testid.endsWith('-end')) return { targetHandle: VIDEO_KEYFRAME_END_HANDLE, zoneKind: 'keyframe' }
    const chipId = chip.getAttribute(VIDEO_KEYFRAME_CHIP_ID_ATTR)
    if (chipId) return { targetHandle: videoKeyframeMiddleHandleId(chipId), zoneKind: 'keyframe' }
  }

  return null
}
